import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { 
  LocationStaffService, 
  LocationStaffAdminDto, 
  StaffStatusDto,
  CreateLocationStaffRequest,
  UpdateLocationStaffRequest
} from '../../../services/location-staff.service';
import { MasterService } from '../../../../../master/services/master.service';
import { MasterModel } from '../../../../../master/models/master.model';

export interface LocationStaffDialogData {
  staff: LocationStaffAdminDto | null;
  locationId: string;
  staffStatuses: StaffStatusDto[];
}

/**
 * Empleados internos/reservados que no deben mostrarse en el selector del
 * diálogo de alta de personal. Se filtran desde el front por nombre.
 */
const HIDDEN_EMPLOYEE_NAMES: string[] = [
  'Empleados_Por_Año',
  'Empleados_Por_Mes',
  'Empleados_Por_Trimestre'
];

@Component({
    selector: 'app-location-staff-dialog',
    templateUrl: './location-staff-dialog.component.html',
    styleUrls: ['./location-staff-dialog.component.css'],
    standalone: false
})
export class LocationStaffDialogComponent implements OnInit {
  staffForm!: UntypedFormGroup;
  isEditMode = false;
  isSaving = false;
  isLoadingEmployees = false;
  errorMessage: string | null = null;

  employees: MasterModel[] = [];
  selectedEmployee: MasterModel | null = null;

  // Color presets for calendar
  colorPresets = [
    '#f44336', '#e91e63', '#9c27b0', '#673ab7',
    '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
    '#009688', '#4caf50', '#8bc34a', '#cddc39',
    '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private staffService: LocationStaffService,
    private masterService: MasterService,
    public dialogRef: MatDialogRef<LocationStaffDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LocationStaffDialogData
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.data.staff;
    this.createForm();
    
    if (this.isEditMode && this.data.staff) {
      this.populateForm(this.data.staff);
    } else {
      // Modo creación: cargar empleados
      this.loadEmployees();
    }
  }

  private createForm(): void {
    if (this.isEditMode) {
      // Modo edición: solo displayName, status y color
      this.staffForm = this.fb.group({
        displayName: ['', [Validators.required, Validators.maxLength(200)]],
        statusMerchantStaffId: [1, [Validators.required]],
        calendarColor: ['']
      });
    } else {
      // Modo creación: incluir selector de empleado
      this.staffForm = this.fb.group({
        employeeId: ['', [Validators.required]],
        displayName: ['', [Validators.required, Validators.maxLength(200)]],
        statusMerchantStaffId: [1, [Validators.required]],
        calendarColor: ['']
      });
    }
  }

  private loadEmployees(): void {
    this.isLoadingEmployees = true;
    this.masterService.getEmployees().subscribe({
      next: (employees) => {
        this.employees = (employees || []).filter(e => !this.isHiddenEmployee(e));
        this.isLoadingEmployees = false;
      },
      error: (err) => {
        console.error('Error loading employees:', err);
        this.errorMessage = 'Error al cargar la lista de empleados';
        this.isLoadingEmployees = false;
      }
    });
  }

  /**
   * Devuelve true si el empleado está en la lista de nombres reservados que
   * no deben aparecer como personal asignable a una location.
   */
  private isHiddenEmployee(employee: MasterModel): boolean {
    const name = (employee && employee.display ? employee.display : '').trim().toLowerCase();
    if (!name) {
      return false;
    }
    const hiddenList = HIDDEN_EMPLOYEE_NAMES || [];
    for (let i = 0; i < hiddenList.length; i++) {
      if ((hiddenList[i] || '').trim().toLowerCase() === name) {
        return true;
      }
    }
    return false;
  }

  onEmployeeSelected(employeeId: string): void {
    this.selectedEmployee = this.employees.find(e => e.id === employeeId) || null;
    if (this.selectedEmployee) {
      // Pre-rellenar displayName con el nombre del empleado
      this.staffForm.patchValue({
        displayName: this.selectedEmployee.display
      });
    }
  }

  private populateForm(staff: LocationStaffAdminDto): void {
    this.staffForm.patchValue({
      displayName: staff.displayName,
      statusMerchantStaffId: staff.statusMerchantStaffId,
      calendarColor: staff.calendarColor || ''
    });
  }

  selectColor(color: string): void {
    this.staffForm.patchValue({ calendarColor: color });
  }

  getDialogTitle(): string {
    return this.isEditMode ? 'Editar Personal' : 'Añadir Personal';
  }

  onSubmit(): void {
    if (this.staffForm.invalid) {
      Object.keys(this.staffForm.controls).forEach(key => {
        const control = this.staffForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;

    const formValue = this.staffForm.value;
    
    if (this.isEditMode && this.data.staff) {
      this.updateStaff(formValue);
    } else {
      this.createStaff(formValue);
    }
  }

  private createStaff(formValue: any): void {
    if (!this.selectedEmployee) {
      this.errorMessage = 'Debe seleccionar un empleado';
      this.isSaving = false;
      return;
    }

    // Extraer datos del empleado del metadata
    const metadata = this.selectedEmployee.metadata || {};
    const firstName = metadata.firstName || this.selectedEmployee.display.split(' ')[0] || '';
    const lastName = metadata.lastName || this.selectedEmployee.display.split(' ').slice(1).join(' ') || '';
    const email = metadata.email || '';
    const phone = metadata.phone || '';

    const request: CreateLocationStaffRequest = {
      employeeExternalId: this.selectedEmployee.id, // El backend espera string que será parseado a Guid
      firstName: firstName,
      lastName: lastName,
      displayName: formValue.displayName,
      email: email,
      phone: phone,
      statusMerchantStaffId: formValue.statusMerchantStaffId,
      calendarColor: formValue.calendarColor || undefined
    };

    this.staffService.createLocationStaff(this.data.locationId, request).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.isSuccess) {
          this.dialogRef.close(true);
        } else {
          this.errorMessage = this.getErrorMessage(response.error);
        }
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Error creating staff:', err);
        this.errorMessage = 'Error al crear el personal. Por favor, intente nuevamente.';
      }
    });
  }

  private updateStaff(formValue: any): void {
    const request: UpdateLocationStaffRequest = {
      firstName: this.data.staff!.firstName,
      lastName: this.data.staff!.lastName,
      displayName: formValue.displayName,
      email: this.data.staff!.email,
      phone: this.data.staff!.phone,
      statusMerchantStaffId: formValue.statusMerchantStaffId,
      calendarColor: formValue.calendarColor || undefined
    };

    this.staffService.updateLocationStaff(
      this.data.locationId,
      this.data.staff!.merchantStaffId,
      request
    ).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.isSuccess) {
          this.dialogRef.close(true);
        } else {
          this.errorMessage = this.getErrorMessage(response.error);
        }
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Error updating staff:', err);
        this.errorMessage = 'Error al actualizar el personal. Por favor, intente nuevamente.';
      }
    });
  }

  private getErrorMessage(error: any): string {
    if (error && error.messages && Array.isArray(error.messages)) {
      return error.messages.join(', ');
    }
    return 'Ha ocurrido un error desconocido';
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  clearError(): void {
    this.errorMessage = null;
  }
  public trackByIndex(i: number) { return i; }

}
